import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  BankCashAccountSchema,
  PaymentDetailSchema,
  PaymentSchema,
  SupplierInvoiceSchema,
  type SupplierDto,
} from '@taqon/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { getPage, newIdempotencyKey, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { refreshAfterPosting } from './cache';
import { RecordLink } from './detail';
import { businessToday } from './line-editor';
import { SupplierPicker } from './lookups';
import { fromFils, isAmount, sum, toFils } from './money';
import { RefusalState } from './refusal';

/**
 * Supplier payments (FRD-FIN-076): the list, and — with the permission — a payment recorded
 * against one or more of the supplier's posted invoices, each allocated in full or in part.
 *
 * An unallocated remainder is held as a supplier advance or prepayment when the payment is posted
 * (FRD-FIN-078), on the account Finance maps for it; it is offset against later invoices from the
 * payment's own page.
 *
 * Creating carries an `Idempotency-Key`, posting carries `If-Match` (SDD §7.3, §7.5). Actions are
 * shown only with their permission; the API remains the enforcement point (SDD §22.4).
 */
export function PaymentsPage() {
  const session = useSession();
  const queryClient = useQueryClient();
  const canCreate = hasCapability(session.data, 'finance.payment:create');
  const canPost = hasCapability(session.data, 'finance.payment:post');
  const payments = useQuery({
    queryKey: ['payments'],
    queryFn: () => getPage('/api/v1/finance/payments?pageSize=50&sort=-document_no', PaymentSchema),
  });
  const post = useMutation({
    mutationFn: (payment: { id: string; version: number }) =>
      postJson(
        `/api/v1/finance/payments/${payment.id}/post`,
        {},
        PaymentSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: payment.version },
      ),
    onSuccess: () => refreshAfterPosting(queryClient),
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Supplier payments"
        description="Pay posted supplier invoices, in full or in part, from a bank or cash register."
      />
      {canCreate && <RecordPayment />}
      {post.isError && <RefusalState error={post.error} />}
      {payments.isError && <ErrorState message={payments.error.message} />}
      <Section title="Payments">
        {payments.isPending && <LoadingState label="Loading payments" />}
        {payments.data?.items.length === 0 && <EmptyState title="No payments yet" />}
        {payments.data !== undefined && payments.data.items.length > 0 && (
          <Table size="small" aria-label="Supplier payments">
            <TableHead>
              <TableRow>
                <TableCell>Document</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Allocated</TableCell>
                <TableCell align="right">Unallocated</TableCell>
                <TableCell>Status</TableCell>
                {canPost && <TableCell align="right">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.data.items.map((payment) => (
                <TableRow key={payment.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <RecordLink to={`/payments/${payment.id}`}>{payment.documentNo}</RecordLink>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{payment.paymentDate}</TableCell>
                  <TableCell align="right">{payment.amount}</TableCell>
                  <TableCell align="right">{payment.allocatedAmount}</TableCell>
                  <TableCell align="right">{payment.unallocatedAmount}</TableCell>
                  <TableCell>
                    <StatusChip status={payment.status} />
                  </TableCell>
                  {canPost && (
                    <TableCell align="right">
                      {payment.status === 'draft' && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={post.isPending}
                          onClick={() => {
                            post.mutate({ id: payment.id, version: payment.version });
                          }}
                        >
                          Post
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
    </Stack>
  );
}

const OPEN_INVOICE_PAGE = 200;

/**
 * A payment against the supplier's open invoices. Each allocation is typed per invoice, capped by
 * what that invoice has outstanding; the totals are exact (whole fils), never floating point.
 */
function RecordPayment() {
  const session = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canSeeRegisters = hasCapability(session.data, 'finance.bank_cash_account:view');
  const [supplier, setSupplier] = useState<SupplierDto | null>(null);
  const [registerId, setRegisterId] = useState('');
  const [paymentDate, setPaymentDate] = useState(() =>
    businessToday(session.data?.businessTimezone),
  );
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [allocations, setAllocations] = useState<Record<string, string>>({});

  // Only active registers take new movements (the API refuses an inactive one).
  const registers = useQuery({
    queryKey: ['bank-cash', 'active'],
    enabled: canSeeRegisters,
    queryFn: () =>
      getPage(
        '/api/v1/finance/bank-cash-accounts?pageSize=200&sort=code&filter[status]=active',
        BankCashAccountSchema,
      ),
  });
  const invoices = useQuery({
    queryKey: ['supplier-invoices', 'open', supplier?.id],
    enabled: supplier !== null,
    queryFn: () =>
      getPage(
        `/api/v1/finance/supplier-invoices?pageSize=${String(OPEN_INVOICE_PAGE)}` +
          `&sort=supplier_invoice_date&filter[status]=posted&filter[open]=true` +
          `&filter[supplier_id]=${supplier?.id ?? ''}`,
        SupplierInvoiceSchema,
      ),
  });
  // Posted, with a payable still outstanding: nothing else can be paid.
  const open = invoices.data?.items ?? [];

  const chosen = open.filter((invoice) => (allocations[invoice.id] ?? '').trim() !== '');
  const allocated = sum(chosen.map((invoice) => allocations[invoice.id] ?? ''));
  const unallocated = fromFils(toFils(amount) - toFils(allocated));
  const problems = [
    ...(amount.trim() !== '' && !isAmount(amount)
      ? ['The payment amount must be a positive amount with at most 2 decimals.']
      : []),
    ...chosen.flatMap((invoice) => {
      const value = allocations[invoice.id] ?? '';
      if (!isAmount(value) || toFils(value) === 0n) {
        return [`${invoice.documentNo}: enter an amount greater than zero, at most 2 decimals.`];
      }
      if (toFils(value) > toFils(invoice.outstandingAmount)) {
        return [`${invoice.documentNo}: only ${invoice.outstandingAmount} is outstanding.`];
      }
      return [];
    }),
    ...(toFils(allocated) > toFils(amount) ? ['The allocations exceed the payment amount.'] : []),
  ];
  const complete =
    supplier !== null &&
    registerId !== '' &&
    paymentDate !== '' &&
    isAmount(amount) &&
    toFils(amount) > 0n &&
    chosen.length > 0;

  const create = useMutation({
    mutationFn: () =>
      postJson(
        '/api/v1/finance/payments',
        {
          supplierId: supplier?.id ?? '',
          paymentDate,
          amount: amount.trim(),
          bankCashAccountId: registerId,
          ...(reference.trim() !== '' ? { reference: reference.trim() } : {}),
          allocations: chosen.map((invoice) => ({
            supplierInvoiceId: invoice.id,
            allocatedAmount: (allocations[invoice.id] ?? '').trim(),
            allocationDate: paymentDate,
          })),
        },
        PaymentDetailSchema,
        session.data?.csrfToken ?? '',
        // One key per deliberate submission: a repeat returns the original document.
        { idempotencyKey: newIdempotencyKey() },
      ),
    onSuccess: async (created) => {
      await refreshAfterPosting(queryClient);
      await navigate(`/payments/${created.payment.id}`);
    },
  });

  return (
    <Section title="Record a payment">
      <Stack
        spacing={2.5}
        sx={{ p: 2.5 }}
        component="form"
        aria-label="Record a payment"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (complete && problems.length === 0 && !create.isPending) create.mutate();
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ minWidth: { md: 280 }, flexGrow: 1 }}>
            <SupplierPicker
              value={supplier}
              onChange={(next) => {
                setSupplier(next);
                setAllocations({});
              }}
            />
          </Box>
          <TextField
            select
            size="small"
            label="Pay from"
            required
            value={registerId}
            disabled={!canSeeRegisters}
            helperText={
              canSeeRegisters
                ? 'An active bank or cash register'
                : 'Needs permission to view bank and cash registers'
            }
            sx={{ minWidth: { md: 260 } }}
            onChange={(e) => {
              setRegisterId(e.target.value);
            }}
          >
            {(registers.data?.items ?? []).map((register) => (
              <MenuItem key={register.id} value={register.id}>
                {register.code} — {register.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            type="date"
            label="Payment date"
            required
            value={paymentDate}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => {
              setPaymentDate(e.target.value);
            }}
          />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            size="small"
            label="Payment amount"
            required
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
            }}
          />
          <TextField
            size="small"
            label="Reference"
            value={reference}
            sx={{ flexGrow: 1 }}
            onChange={(e) => {
              setReference(e.target.value);
            }}
          />
        </Stack>

        {supplier === null ? (
          <Typography variant="body2" color="text.secondary">
            Choose a supplier to see their posted invoices with a payable outstanding.
          </Typography>
        ) : (
          <OpenInvoices
            loading={invoices.isPending}
            error={invoices.isError ? invoices.error.message : null}
            truncated={(invoices.data?.total ?? 0) > OPEN_INVOICE_PAGE}
            invoices={open}
            allocations={allocations}
            onAllocate={(invoiceId, value) => {
              setAllocations((current) => ({ ...current, [invoiceId]: value }));
            }}
          />
        )}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0.5, sm: 3 }}
          role="status"
          aria-label="Allocation totals"
        >
          <Typography variant="body2">
            Allocated: <strong>{allocated}</strong>
          </Typography>
          <Typography variant="body2">
            Unallocated: <strong>{isAmount(amount) ? unallocated : '—'}</strong>
          </Typography>
          {chosen.length > 0 && (
            <Button
              size="small"
              onClick={() => {
                setAmount(allocated);
              }}
            >
              Pay the allocated total
            </Button>
          )}
        </Stack>
        {isAmount(amount) && toFils(unallocated) > 0n && (
          <Alert severity="info" variant="outlined">
            {unallocated} is not allocated. When the payment is posted it is held as a supplier
            advance, to be offset against later invoices of this supplier.
          </Alert>
        )}
        {problems.length > 0 && (
          <Alert severity="warning" variant="outlined">
            {problems.map((p) => (
              <div key={p}>{p}</div>
            ))}
          </Alert>
        )}
        {create.isError && <RefusalState error={create.error} />}
        <Box>
          <Button
            type="submit"
            variant="contained"
            disabled={!complete || problems.length > 0 || create.isPending}
          >
            {create.isPending ? 'Saving…' : 'Save payment as draft'}
          </Button>
        </Box>
      </Stack>
    </Section>
  );
}

function OpenInvoices({
  loading,
  error,
  truncated,
  invoices,
  allocations,
  onAllocate,
}: {
  loading: boolean;
  error: string | null;
  truncated: boolean;
  invoices: readonly {
    id: string;
    documentNo: string;
    supplierInvoiceNumber: string;
    supplierInvoiceDate: string;
    dueDate: string | null;
    outstandingAmount: string;
  }[];
  allocations: Readonly<Record<string, string>>;
  onAllocate: (invoiceId: string, value: string) => void;
}) {
  if (loading) return <LoadingState label="Loading open invoices" />;
  if (error !== null) return <ErrorState message={error} />;
  if (invoices.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        This supplier has no posted invoice with a payable outstanding.
      </Typography>
    );
  }
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label="Open invoices">
        <TableHead>
          <TableRow>
            <TableCell>Invoice</TableCell>
            <TableCell>Supplier reference</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Due</TableCell>
            <TableCell align="right">Outstanding</TableCell>
            <TableCell>Allocate</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoices.map((invoice) => (
            <TableRow key={invoice.id}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <RecordLink to={`/supplier-invoices/${invoice.id}`}>
                  {invoice.documentNo}
                </RecordLink>
              </TableCell>
              <TableCell>{invoice.supplierInvoiceNumber}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.supplierInvoiceDate}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.dueDate ?? '—'}</TableCell>
              <TableCell align="right">{invoice.outstandingAmount}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <TextField
                    size="small"
                    value={allocations[invoice.id] ?? ''}
                    slotProps={{
                      htmlInput: { 'aria-label': `Allocate to ${invoice.documentNo}` },
                    }}
                    sx={{ width: 130 }}
                    onChange={(e) => {
                      onAllocate(invoice.id, e.target.value);
                    }}
                  />
                  <Button
                    size="small"
                    aria-label={`Allocate the outstanding of ${invoice.documentNo}`}
                    onClick={() => {
                      onAllocate(invoice.id, invoice.outstandingAmount);
                    }}
                  >
                    Full
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {truncated && (
        <Typography variant="caption" color="text.secondary">
          Showing the oldest {OPEN_INVOICE_PAGE} open invoices of this supplier.
        </Typography>
      )}
    </Box>
  );
}
